/**
 * Workflow definitions page — first-class management UI for the
 * authored DSL store.
 *
 * Subscriptions point at a frozen WorkflowDefinition version via FK.
 * This page lets users:
 *   - See every definition in their org, one row per ``WorkflowKey``
 *     (the shown version per row, with version count + last-updated),
 *     paged / searched / sorted / filtered SERVER-SIDE via
 *     ``GET /workflows/definitions/grid`` — the page never holds more
 *     than one page of DSL blobs and never slices, sorts or filters the
 *     rows it was given
 *   - Create v1 of a brand-new definition
 *   - Bump a new version of an existing definition
 *   - Inspect the JSON DSL for any version
 *
 * Companion to ``/portal/workflows/subscriptions`` which now accepts
 * a ``definition_id`` rather than embedding DSL inline.
 *
 * Auth via portalFetch + JWT. RBAC enforced server-side at
 * /api/v1/gateway/workflows/* — same surface as the subscriptions
 * page.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import Editor from "@monaco-editor/react";
import {
  AlertCircle,
  AlertTriangle,
  Eye,
  FolderPlus,
  FolderTree,
  LayoutTemplate,
  Loader2,
  MoreHorizontal,
  Play,
  Plus,
  RefreshCw,
  ShieldOff,
  Trash2,
  Wrench,
  WorkflowIcon,
} from "lucide-react";

import { Inspector } from "@/components/portal/workflow-flow-v2/Inspector";
import { layoutFlowGraph } from "@/components/portal/workflow-flow-v2/layout";
import { ReadOnlyCanvas } from "@/components/portal/workflow-flow-v2/ReadOnlyCanvas";
import { TriggersPanel } from "@/components/portal/workflow-flow-v2/TriggersPanel";
import { WorkflowConfigPanel } from "@/components/portal/workflow-flow-v2/WorkflowConfigPanel";
import type { DslWorkflow } from "@/components/portal/workflow-flow-v2/types";
import {
  configMapFromWorkflow,
  toFlowGraph,
} from "@/lib/workflow-graph-adapter";
import { validateTopology } from "@/lib/workflow-validation";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import {
  hasScheduleTrigger,
  scheduleLabel,
} from "@/components/portal/workflow-flow-v2/workflowScheduleLabel";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollableTable } from "@/components/ui/scrollable-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { usePermissions } from "@/hooks/usePermissions";
import { workflowsApi } from "@/services/api/workflows";
import type {
  DefinitionGridRow,
  DefinitionResponse,
  DefinitionSortField,
  DefinitionSummary,
  MembershipEntry,
  SubscriptionDrift,
  WorkflowLastRun,
  WorkflowProject,
} from "@/services/api/workflows";
import { workflowDraftsApi } from "@/services/api/workflowDrafts";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { WorkflowDraftsPanel } from "@/components/portal/workflows/WorkflowDraftsPanel";
import {
  LastRunsUnavailable,
  WorkflowLastRunCell,
} from "@/components/portal/workflow-flow-v2/workflowLastRun";
import { definitionBlurb } from "./-workflowDefinition";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Route = createFileRoute("/portal/workflows/definitions")({
  component: DefinitionsPage,
  // `?tab=drafts` opens the Drafts tab — where the builder returns a person
  // after they publish or abandon a Claude draft.
  validateSearch: (search: Record<string, unknown>): { tab?: "drafts" } =>
    search.tab === "drafts" ? { tab: "drafts" } : {},
});

/** The Enabled filter's three states → the grid's ``is_enabled`` param. */
type EnabledFilter = "all" | "enabled" | "disabled";

/** Sentinel for "no project filter" — a real project id is a UUID, so it
 *  cannot collide. */
const ALL_PROJECTS = "__all__";

/** Short absolute date for the Updated column; the ISO string is on the
 *  title so the exact instant is one hover away. */
const updatedAtFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

function DefinitionsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { isAdmin, isPlatformAdmin } = usePermissions();
  // Workflow authoring is owner+admin (and platform staff). The gateway
  // enforces this on /gateway/workflows/* (owner=.*, admin=CRUD, member=none);
  // we gate the UI to match. NB: do NOT use can("manage","automation") here —
  // "automation" resolves to the *agentic* gateway path, a different policy
  // surface, which both locked admins out and 403-flashed members.
  const canManage = isAdmin || isPlatformAdmin;

  // Definitions | Drafts (plan mcp-artefact-creation-pilot D15). The Drafts
  // tab lists workflows Claude drafted; nothing in it runs until a person
  // publishes it through the builder. The count is read on mount so the tab
  // says there is something waiting before anyone opens it.
  const { tab: initialTab } = Route.useSearch();
  const selectedOrganisationId = usePortalData().selectedOrganisation?.organisationId;
  const [pageTab, setPageTab] = useState<"definitions" | "drafts">(
    initialTab ?? "definitions",
  );
  const [openDraftCount, setOpenDraftCount] = useState<number | null>(null);
  useEffect(() => {
    if (!token || !canManage || !selectedOrganisationId) return;
    let cancelled = false;
    workflowDraftsApi
      .list(token, "draft")
      .then((rows) => {
        if (!cancelled) setOpenDraftCount(rows.length);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // The org is a dependency: portalFetch scopes each call to the selected
    // org, and an org switch does not remount this page.
  }, [token, canManage, selectedOrganisationId]);

  // The page of rows as rendered. Mirrors the hook's fetched page (see the
  // effect below) so the Enabled kill switch can flip a row optimistically and
  // then sync the authoritative row back, exactly as before the grid moved
  // server-side. Everything else about the page — window, order, search,
  // filters — lives in the hook and the state feeding its fetchFn.
  const [rows, setRows] = useState<DefinitionGridRow[]>([]);

  // Grid controls. The search term is debounced before it reaches the hook's
  // dependencies so a pause — not every keystroke — resets to page 1 and
  // refetches once (D12). Filters are plain state: each change is a refetch.
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>("all");
  const [projectFilter, setProjectFilter] = useState<string>(ALL_PROJECTS);

  // View dialog — read-only inspect of a row's DSL.
  const [viewing, setViewing] = useState<DefinitionResponse | null>(null);

  // Delete confirmation — retires a whole WorkflowKey (all versions + subs).
  // Destructive + irreversible, so it requires typing the workflow key to
  // confirm (same posture as the connector Disconnect flow).
  const [deletingRow, setDeletingRow] = useState<DefinitionGridRow | null>(
    null,
  );
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Workflow keys currently being fired via "Run now" — each disables its own
  // menu item + shows a spinner while the dispatcher runs in-process. A set
  // (not a scalar) so firing two different workflows in quick succession
  // tracks each row's spinner independently.
  const [runningKeys, setRunningKeys] = useState<Set<string>>(new Set());

  // Project grouping context for the "Project" column + "Add to project" picker.
  const [memberships, setMemberships] = useState<MembershipEntry[]>([]);
  const [projects, setProjects] = useState<WorkflowProject[]>([]);
  // Last-run + overdue signal per workflow_key (additive, best-effort).
  const [lastRuns, setLastRuns] = useState<WorkflowLastRun[]>([]);
  const [lastRunsFailed, setLastRunsFailed] = useState(false);
  // Subscription drift per workflow_key — a trigger listening to something the
  // DSL no longer says. Invisible without this: the row reads healthy
  // (IsActive=1, current version) while matching events nothing emits. Also
  // additive/best-effort, so a hiccup here cannot blank the definitions list.
  const [drift, setDrift] = useState<SubscriptionDrift[]>([]);
  const [repairingKeys, setRepairingKeys] = useState<Set<string>>(new Set());
  const [repairResult, setRepairResult] = useState<string | null>(null);

  /**
   * One page of the grid, every axis server-side. Errors propagate: the
   * definitions list is authoritative, and its failure errors the page (the
   * hook surfaces it as `error`). Members have no gateway access to
   * /definitions — don't fire the call for them (it would 403); the no-access
   * view renders instead.
   */
  const fetchDefinitions = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<DefinitionGridRow>> => {
      if (!canManage) return { data: [], totalCount: 0, page, pageSize };
      const res = await workflowsApi.listDefinitionsGrid(token ?? undefined, {
        search: debouncedSearch.trim() || undefined,
        isEnabled:
          enabledFilter === "all" ? undefined : enabledFilter === "enabled",
        projectId: projectFilter === ALL_PROJECTS ? undefined : projectFilter,
        sortBy: sort?.key as DefinitionSortField | undefined,
        sortOrder: sort?.direction,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      return { data: res.rows, totalCount: res.total, page, pageSize };
    },
    [token, canManage, debouncedSearch, enabledFilter, projectFilter],
  );

  const {
    data: pageRows,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<DefinitionGridRow>({
    fetchFn: fetchDefinitions,
    initialPageSize: 25,
    initialSort: { key: "name", direction: "asc" },
    dependencies: [token, debouncedSearch, enabledFilter, projectFilter],
  });

  // A freshly fetched page replaces whatever optimistic state the previous
  // page carried.
  useEffect(() => {
    setRows(pageRows);
  }, [pageRows]);

  /**
   * The per-key feeds behind the Project / Last run / drift cells. Org-wide,
   * key-indexed, no DSL — fetched once on mount and on Refresh rather than per
   * page turn (D11): a page turn brings no new information for them. Each is
   * best-effort so a hiccup in one endpoint can't blank the definitions list.
   */
  const loadFeeds = useCallback(async () => {
    const [mships, projs, runs, drifted] = await Promise.all([
      workflowsApi
        .listProjectMemberships(token ?? undefined)
        .catch(() => [] as MembershipEntry[]),
      workflowsApi
        .listProjects(token ?? undefined)
        .catch(() => [] as WorkflowProject[]),
      workflowsApi
        .listLastRuns(token ?? undefined)
        .then((r) => r.rows)
        .catch(() => null),
      workflowsApi
        .listSubscriptionDrift(token ?? undefined)
        .catch(() => [] as SubscriptionDrift[]),
    ]);
    // Array-guard every additive feed. `.catch()` only covers a REJECTED
    // promise; a 200 carrying the wrong shape sails through and then throws
    // inside a useMemo — which white-screens the whole page ("memberships is
    // not iterable"). These columns are decoration; the definitions list is
    // the thing the operator came for and must survive them.
    setMemberships(Array.isArray(mships) ? mships : []);
    setProjects(Array.isArray(projs) ? projs : []);
    setDrift(Array.isArray(drifted) ? drifted : []);
    setLastRuns(Array.isArray(runs) ? runs : []);
    setLastRunsFailed(runs === null);
  }, [token]);

  useEffect(() => {
    // Same gate as the grid fetch — members would 403 on every feed.
    if (canManage) void loadFeeds();
  }, [loadFeeds, canManage]);

  /** Refresh = the feeds AND the current page, in that order. */
  const handleRefresh = useCallback(() => {
    void loadFeeds();
    refresh();
  }, [loadFeeds, refresh]);

  /**
   * Refetch after a row LEFT the grid (deleted, or paused while the Enabled
   * filter is showing only enabled rows). If it was this page's only row and
   * we are past page 1, step back instead of refetching the same window — the
   * hook does not move `page` when `total` shrinks, so a plain refresh would
   * land on an empty page past the end.
   */
  const refetchAfterRowLeft = useCallback(() => {
    if (rows.length === 1 && pagination.page > 1) prevPage();
    else refresh();
  }, [rows.length, pagination.page, prevPage, refresh]);

  // workflow_key → its project membership (for the Project column).
  const projectByKey = useMemo(() => {
    const m = new Map<string, MembershipEntry>();
    for (const e of memberships) m.set(e.workflow_key, e);
    return m;
  }, [memberships]);

  // workflow_key → last-run signal (for the "Last run" column).
  const lastRunByKey = useMemo(() => {
    const m = new Map<string, WorkflowLastRun>();
    for (const r of lastRuns) m.set(r.workflow_key, r);
    return m;
  }, [lastRuns]);

  const driftByKey = useMemo(() => {
    const m = new Map<string, SubscriptionDrift[]>();
    for (const d of drift) {
      const list = m.get(d.workflow_key) ?? [];
      list.push(d);
      m.set(d.workflow_key, list);
    }
    return m;
  }, [drift]);

  /**
   * Repair one workflow's subscriptions from its ACTIVE version.
   *
   * Deliberately not a republish: no new version is created, so the version
   * history keeps meaning "someone changed the workflow".
   */
  const handleRepair = useCallback(
    async (workflowKey: string) => {
      setRepairResult(null);
      setRepairingKeys((prev) => new Set(prev).add(workflowKey));
      try {
        const res = await workflowsApi.reconcileSubscriptions(
          workflowKey,
          token ?? undefined,
        );
        const failed = res.triggers.filter((t) => t.action === "failed");
        if (!res.ok || failed.length > 0) {
          // The endpoint answers 200 with ok=false for a per-trigger refusal,
          // so checking the status alone would report a silent success.
          setRepairResult(
            `${workflowKey}: ${failed.length} trigger(s) could not be repaired — ` +
              failed.map((t) => `${t.trigger_id}: ${t.error}`).join("; "),
          );
        } else {
          const moved = res.triggers.filter((t) => t.action === "updated");
          setRepairResult(
            `${workflowKey}: repaired ${moved.length} trigger(s) on v${res.version} — no new version created.`,
          );
        }
      } catch (e) {
        setRepairResult(
          e instanceof Error ? e.message : `${workflowKey}: repair failed.`,
        );
      } finally {
        setRepairingKeys((prev) => {
          const next = new Set(prev);
          next.delete(workflowKey);
          return next;
        });
        // Re-read drift either way — a partial repair must not leave a stale
        // "all clear" on screen.
        await loadFeeds();
      }
    },
    [token, loadFeeds],
  );

  // Re-pull just the last-run signal after firing a workflow, so the "Last run"
  // cell reflects the run the operator just triggered (clearing any overdue
  // flag) without a full page reload. Best-effort — a failure leaves the prior
  // values rather than blanking the column.
  const refreshLastRuns = useCallback(async () => {
    try {
      const res = await workflowsApi.listLastRuns(token ?? undefined);
      setLastRuns(res.rows);
      setLastRunsFailed(false);
    } catch {
      /* keep the existing signal */
    }
  }, [token]);

  /** Add a single workflow to a project from the Definitions list, then refresh
   * the membership map so the Project column updates in place. */
  const handleAddToProject = useCallback(
    async (workflowKey: string, projectId: string, projectName: string) => {
      try {
        const res = await workflowsApi.addProjectMembersBulk(
          projectId,
          [workflowKey],
          token ?? undefined,
        );
        // The bulk endpoint reports — not errors — a key that already belongs
        // to another project. Honour that: don't claim success or point the
        // badge at the wrong project.
        if (res.skipped.some((s) => s.workflow_key === workflowKey)) {
          toast.warning(
            `${workflowKey} already belongs to another project — not added.`,
          );
          return;
        }
        setMemberships((prev) => [
          ...prev.filter((e) => e.workflow_key !== workflowKey),
          { workflow_key: workflowKey, project_id: projectId, project_name: projectName },
        ]);
        toast.success(`Added ${workflowKey} to ${projectName}`);
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Failed to add to project",
        );
      }
    },
    [token],
  );

  /** Flip the kill switch on a WorkflowKey. Optimistic update so the
   * Switch reflects the new state immediately; reverts on API error
   * and surfaces a toast. */
  const handleToggleEnabled = useCallback(
    async (workflowKey: string, nextEnabled: boolean) => {
      // Optimistic update of the shown row — one row per key on this grid.
      setRows((prev) =>
        prev.map((r) =>
          r.workflow_key === workflowKey ? { ...r, is_enabled: nextEnabled } : r,
        ),
      );
      try {
        const updated = await workflowsApi.setDefinitionEnabled(
          workflowKey,
          nextEnabled,
          token ?? undefined,
        );
        // Sync with the authoritative row from the API in case any
        // server-side derivations drift (e.g. UpdatedAtUtc). Spread over the
        // grid row so version_count / updated_at_utc survive the merge.
        setRows((prev) =>
          prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
        );
        toast.success(
          nextEnabled
            ? `Workflow ${workflowKey} resumed`
            : `Workflow ${workflowKey} paused`,
        );
        // Under an Enabled filter the row no longer matches — let the server
        // say so rather than leaving a stale row on a filtered page.
        if (enabledFilter !== "all") refetchAfterRowLeft();
      } catch (e) {
        // Revert the optimistic flip on failure.
        setRows((prev) =>
          prev.map((r) =>
            r.workflow_key === workflowKey
              ? { ...r, is_enabled: !nextEnabled }
              : r,
          ),
        );
        const message =
          e instanceof Error ? e.message : "Failed to update workflow";
        toast.error(message);
      }
    },
    [token, enabledFilter, refetchAfterRowLeft],
  );

  /** Fire a schedule-driven workflow on demand — same as a cron tick. Surfaces
   * the count fired (one execution per fanOut row) so the operator gets
   * immediate confirmation without waiting for the next scheduled run. */
  const handleRunNow = useCallback(
    async (workflowKey: string) => {
      setRunningKeys((s) => new Set(s).add(workflowKey));
      try {
        const res = await workflowsApi.runScheduleNow(
          workflowKey,
          token ?? undefined,
        );
        if (res.executions_fired === 0) {
          toast.info(
            `No work for ${workflowKey} right now — nothing matched the discovery query.`,
          );
        } else {
          // Link straight to what just ran: a single execution opens its
          // detail drawer; several land on the list pre-filtered to the workflow.
          const single =
            res.execution_ids.length === 1 ? res.execution_ids[0] : undefined;
          toast.success(
            `Fired ${res.executions_fired} execution${res.executions_fired === 1 ? "" : "s"} for ${workflowKey}`,
            {
              action: {
                label: "View",
                onClick: () =>
                  void navigate({
                    to: "/portal/workflows/executions",
                    search: single
                      ? { open: single }
                      : { workflow: workflowKey },
                  }),
              },
            },
          );
          void refreshLastRuns();
        }
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Failed to run workflow now",
        );
      } finally {
        setRunningKeys((s) => {
          const next = new Set(s);
          next.delete(workflowKey);
          return next;
        });
      }
    },
    [token, navigate, refreshLastRuns],
  );

  const handleDelete = useCallback(async () => {
    if (!deletingRow) return;
    const key = deletingRow.workflow_key;
    setIsDeleting(true);
    try {
      const res = await workflowsApi.deleteDefinition(key, token ?? undefined);
      toast.success(
        `Deleted ${key} (${res.versions_deleted} version${res.versions_deleted === 1 ? "" : "s"})`,
      );
      setDeletingRow(null);
      setDeleteConfirmText("");
      // If the View dialog is open on the workflow we just deleted, close it —
      // its Triggers/Config tabs would otherwise 404 against a gone key.
      setViewing((v) => (v?.workflow_key === key ? null : v));
      void loadFeeds();
      refetchAfterRowLeft();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete workflow");
    } finally {
      setIsDeleting(false);
    }
  }, [deletingRow, token, loadFeeds, refetchAfterRowLeft]);

  const hasActiveFilter =
    searchTerm.trim() !== "" ||
    enabledFilter !== "all" ||
    projectFilter !== ALL_PROJECTS;

  // Members (and any non-admin) have no access to the workflows area — show a
  // clean no-access page instead of a 403-flashing table.
  if (!canManage) {
    return (
      <>
        <Helmet>
          <title>Workflow Definitions | Portal</title>
        </Helmet>
        <div className="space-y-6">
          <PageHeader
            title="Workflow Definitions"
            description="Author and version the workflow DSL once; reference it from one or many subscriptions."
            icon={WorkflowIcon}
            eyebrow="Authoring"
          />
          <Alert>
            <ShieldOff className="h-4 w-4" />
            <AlertDescription>
              Workflows are managed by organisation owners and admins. You don't
              have access to this area.
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Workflow Definitions | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Workflow Definitions"
          description="Author and version the workflow DSL once; reference it from one or many subscriptions."
          icon={WorkflowIcon}
          eyebrow="Authoring"
        >
          {/* Templates apply DDL to a customer DB — platform admins only, as
              the dp route enforces (require_platform_admin). */}
          {isPlatformAdmin && (
            <Button
              variant="outline"
              onClick={() => void navigate({ to: "/portal/workflows/templates" })}
            >
              <LayoutTemplate className="mr-2 h-4 w-4" />
              Templates
            </Button>
          )}
          <Button
            onClick={() =>
              void navigate({ to: "/portal/workflows/definitions/new" })
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            New definition
          </Button>
        </PageHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {lastRunsFailed && <LastRunsUnavailable />}

        {repairResult && (
          <Alert data-testid="repair-result">
            <Wrench className="h-4 w-4" />
            <AlertDescription>{repairResult}</AlertDescription>
          </Alert>
        )}

        <Tabs
          value={pageTab}
          onValueChange={(v) => setPageTab(v as "definitions" | "drafts")}
        >
          <TabsList>
            <TabsTrigger value="definitions">Definitions</TabsTrigger>
            <TabsTrigger value="drafts">
              Drafts
              {openDraftCount ? (
                <Badge variant="secondary" className="ml-2 px-1.5 text-xs">
                  {openDraftCount}
                </Badge>
              ) : null}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {pageTab === "drafts" && (
          <WorkflowDraftsPanel onCountChange={setOpenDraftCount} />
        )}

        {/* One content panel: controls bar, then the table, then the pager.
            Every control below drives a server round-trip — nothing here
            narrows or reorders the page it was given. Hidden, not unmounted,
            on the Drafts tab so its filters and page survive a tab switch. */}
        <div
          hidden={pageTab !== "definitions"}
          className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6"
        >
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by workflow key or name..."
              containerClassName="flex-1 min-w-[200px]"
              aria-label="Search definitions"
              // BE = FE: the grid 422s a search over 200 chars
              // (lara-data-plane workflows/api/v2/grid_params.py); cap it
              // here so the operator never sees that 422 as an empty grid.
              maxLength={200}
            />
            <Select
              value={enabledFilter}
              onValueChange={(v) => setEnabledFilter(v as EnabledFilter)}
            >
              <SelectTrigger className="w-[140px]" aria-label="Enabled filter">
                <SelectValue className="truncate" placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="enabled">Enabled</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[140px]" aria-label="Project filter">
                <SelectValue className="truncate" placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PROJECTS}>All projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              aria-label="Refresh"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          {isLoading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <WorkflowIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium">
                {hasActiveFilter
                  ? "No definitions match your search"
                  : "No definitions yet"}
              </h3>
              <p className="mb-4 text-muted-foreground">
                {hasActiveFilter
                  ? "Try a different search term or filter."
                  : 'Click "New definition" to author your first workflow.'}
              </p>
            </div>
          ) : (
            <>
              <ScrollableTable>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead
                        direction={getSortDirection("workflow_key")}
                        onSort={() => requestSort("workflow_key")}
                      >
                        Workflow key
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getSortDirection("name")}
                        onSort={() => requestSort("name")}
                      >
                        Name
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getSortDirection("version")}
                        onSort={() => requestSort("version")}
                      >
                        Version
                      </SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled>
                        Schedule
                      </SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled>
                        Last run
                      </SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled>
                        Project
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getSortDirection("updated_at")}
                        onSort={() => requestSort("updated_at")}
                      >
                        Updated
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getSortDirection("is_enabled")}
                        onSort={() => requestSort("is_enabled")}
                        className="text-center"
                      >
                        Enabled
                      </SortableTableHead>
                      <SortableTableHead
                        direction={null}
                        onSort={() => {}}
                        disabled
                        className="w-[50px]"
                      >
                        {""}
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => {
                      const rowDrift = driftByKey.get(row.workflow_key);
                      const membership = projectByKey.get(row.workflow_key);
                      const blurb = definitionBlurb(row);
                      const updatedAt = new Date(row.updated_at_utc);
                      return (
                        <TableRow key={row.workflow_key}>
                          <TableCell className="font-mono text-xs">
                            {row.workflow_key}
                            {rowDrift?.length ? (
                              <div
                                className="mt-1 flex items-start gap-1 font-sans text-xs text-amber-700"
                                data-testid={`drift-${row.workflow_key}`}
                                title={rowDrift
                                  .map((x) => `${x.trigger_id}: ${x.reason}`)
                                  .join("\n")}
                              >
                                <AlertTriangle className="mt-0.5 h-3 w-3 flex-none" />
                                <span>
                                  {rowDrift.length} trigger
                                  {rowDrift.length === 1 ? "" : "s"} not
                                  listening to what this workflow says
                                </span>
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {/* The name opens the View dialog — the row's
                                primary action, one click without the menu. */}
                            <button
                              type="button"
                              className="text-left font-medium hover:underline"
                              onClick={() => setViewing(row)}
                            >
                              {row.name}
                            </button>
                            {blurb && (
                              <div
                                className="mt-0.5 max-w-[42ch] truncate text-xs text-muted-foreground"
                                title={blurb}
                              >
                                {blurb}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">v{row.version}</Badge>
                            {row.version_count > 1 && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                ({row.version_count} versions)
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {scheduleLabel(row.workflow)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <WorkflowLastRunCell
                              run={lastRunByKey.get(row.workflow_key)}
                            />
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {membership ? (
                              // Grouped → badge linking to the project.
                              <Badge
                                variant="secondary"
                                className="cursor-pointer hover:bg-secondary/70"
                                onClick={() =>
                                  void navigate({
                                    to: "/portal/workflows/projects/$projectId",
                                    params: { projectId: membership.project_id },
                                  })
                                }
                              >
                                <FolderTree className="mr-1 h-3 w-3" />
                                {membership.project_name}
                              </Badge>
                            ) : (
                              // Ungrouped → "Add to project" lives in the row menu.
                              <span className="text-xs text-muted-foreground">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell
                            className="whitespace-nowrap text-sm text-muted-foreground"
                            title={updatedAt.toISOString()}
                          >
                            {updatedAtFormatter.format(updatedAt)}
                          </TableCell>
                          <TableCell className="text-center">
                            {/* Kill switch on the shown version. Paused
                                workflows still appear in the table; the
                                dispatcher acks events without firing them
                                (visible as workflows.subscription.disabled
                                in observability). */}
                            <Switch
                              checked={row.is_enabled}
                              onCheckedChange={(next) =>
                                void handleToggleEnabled(row.workflow_key, next)
                              }
                              disabled={!canManage}
                              aria-label={`Toggle ${row.workflow_key}`}
                            />
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Actions for ${row.workflow_key}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setViewing(row)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </DropdownMenuItem>
                                {canManage && hasScheduleTrigger(row.workflow) && (
                                  <DropdownMenuItem
                                    disabled={runningKeys.has(row.workflow_key)}
                                    onClick={() =>
                                      void handleRunNow(row.workflow_key)
                                    }
                                    aria-label={`Run ${row.workflow_key} now`}
                                  >
                                    {runningKeys.has(row.workflow_key) ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Run now
                                  </DropdownMenuItem>
                                )}
                                {canManage && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      void navigate({
                                        to: "/portal/workflows/definitions/$key/new-version",
                                        params: { key: row.workflow_key },
                                      })
                                    }
                                  >
                                    <Plus className="mr-2 h-4 w-4" />
                                    New version
                                  </DropdownMenuItem>
                                )}
                                {canManage && !membership && projects.length > 0 && (
                                  <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                      <FolderPlus className="mr-2 h-4 w-4" />
                                      Add to project
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                      {projects.map((p) => (
                                        <DropdownMenuItem
                                          key={p.id}
                                          onClick={() =>
                                            void handleAddToProject(
                                              row.workflow_key,
                                              p.id,
                                              p.name,
                                            )
                                          }
                                        >
                                          {p.name}
                                        </DropdownMenuItem>
                                      ))}
                                    </DropdownMenuSubContent>
                                  </DropdownMenuSub>
                                )}
                                {canManage && rowDrift?.length ? (
                                  <DropdownMenuItem
                                    disabled={repairingKeys.has(row.workflow_key)}
                                    onClick={() => void handleRepair(row.workflow_key)}
                                    data-testid={`repair-${row.workflow_key}`}
                                    aria-label={`Repair subscriptions for ${row.workflow_key}`}
                                    title="Re-derives what each trigger listens to from this workflow's active version. Does not publish a new version."
                                  >
                                    {repairingKeys.has(row.workflow_key) ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Wrench className="mr-2 h-4 w-4" />
                                    )}
                                    Repair subscriptions
                                  </DropdownMenuItem>
                                ) : null}
                                {canManage && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-rose-600 focus:text-rose-700"
                                      onClick={() => setDeletingRow(row)}
                                      aria-label={`Delete ${row.workflow_key}`}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollableTable>
            </>
          )}
          {/* Outside the table/empty ternary on purpose: a page past the end
              (its last row just left) shows the empty state AND the pager, so
              the operator can step back. */}
            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
        </div>
      </div>

      {/* View dialog — read-only inspect. Same Canvas/JSON tabs as the
          authoring surface (WorkflowAuthor) but with no edit handles —
          ReadOnlyCanvas drops the drag/drop/connect interactions; the
          Monaco editor is set readOnly. Matches the "see the shape" UX
          authors get when they open the same workflow for a new version. */}
      <Dialog
        open={viewing !== null}
        onOpenChange={(open) => !open && setViewing(null)}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              {viewing?.workflow_key} — v{viewing?.version ?? ""}
            </DialogTitle>
            <DialogDescription>{viewing?.name}</DialogDescription>
          </DialogHeader>
          {viewing ? (
            <>
            <WhatThisDoes definition={viewing} />
            <ViewWorkflowTabs
              workflow={viewing.workflow as unknown as DslWorkflow}
              workflowKey={viewing.workflow_key}
              definitionId={viewing.id}
              summary={viewing.summary}
              onPublished={(next) => {
                // A new version is the row's new state; the grid shows the
                // version, so refetch the page too.
                setViewing(next);
                refresh();
              }}
            />
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — retires the whole WorkflowKey */}
      <AlertDialog
        open={!!deletingRow}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingRow(null);
            setDeleteConfirmText("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this workflow?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This permanently deletes{" "}
                  <strong>{deletingRow?.workflow_key}</strong> — every version
                  and its triggers/subscriptions. It stops firing immediately.
                </p>
                <p className="text-xs text-slate-500">
                  Past execution history is kept for audit. This can't be
                  undone — to re-add it you'd publish a new definition.
                </p>
                <div>
                  <Label
                    htmlFor="delete-confirm"
                    className="text-sm font-medium text-slate-700"
                  >
                    Type{" "}
                    <code className="rounded bg-slate-100 px-1 text-rose-600">
                      {deletingRow?.workflow_key}
                    </code>{" "}
                    to confirm
                  </Label>
                  <Input
                    id="delete-confirm"
                    className="mt-1"
                    autoComplete="off"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.currentTarget.value)}
                    placeholder={deletingRow?.workflow_key}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={
                isDeleting ||
                deleteConfirmText !== (deletingRow?.workflow_key ?? "")
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting
                </span>
              ) : (
                "Delete workflow"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


type TabName = "canvas" | "triggers" | "config" | "json";

/** "What this does" — intent (when the author gave one) above the structure
 * derived from the graph. Renders nothing at all when the backend sent no
 * summary, so an older data-plane degrades rather than showing an empty box. */
function WhatThisDoes({ definition }: { definition: DefinitionResponse }) {
  const summary = definition.summary;
  // The backend resolves the precedence (typed description, else the workflow's
  // `$comment`); `intent_full` is the untruncated version for this panel, where
  // there is room for it. The DSL read is the older-data-plane fallback only.
  const legacyDescription = (definition.workflow as { description?: unknown })
    ?.description;
  const intent =
    summary?.intent_full?.trim() ||
    summary?.intent?.trim() ||
    (typeof legacyDescription === "string" ? legacyDescription.trim() : "") ||
    null;
  if (!intent && !summary) return null;

  return (
    <div className="mb-4 rounded-md border bg-muted/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        What this does
      </div>
      {intent && <p className="mt-1.5 text-sm">{intent}</p>}
      {summary && (
        <div className="mt-2 space-y-1 text-sm">
          <div>
            <span className="text-muted-foreground">Runs: </span>
            {summary.trigger}
          </div>
          {(summary.step_lines?.length ?? 0) > 0 ? (
            <ol className="ml-4 list-decimal space-y-1 text-muted-foreground">
              {summary.step_lines!.map((step, i) => (
                <li key={`${i}-${step.line}`}>
                  {step.node_id ? (
                    <>
                      <code className="rounded bg-muted px-1 text-xs">
                        {step.node_id}
                      </code>{" "}
                      {step.line.replace(`${step.node_id} — `, "")}
                    </>
                  ) : (
                    step.line
                  )}
                  {/* The author's comments run long — the topic sentence is the
                      line, the rest is one click away rather than a wall.
                      Compared against the UNPREFIXED line: `line` carries the
                      node id, so comparing raw would never match and a
                      one-sentence comment would get a "more" that just repeats
                      the sentence above it. */}
                  {step.detail &&
                    step.detail.trim() !==
                      (step.node_id
                        ? step.line.replace(`${step.node_id} — `, "")
                        : step.line
                      ).trim() && (
                    <details className="mt-0.5">
                      <summary className="cursor-pointer text-xs text-muted-foreground/70 hover:text-muted-foreground">
                        more
                      </summary>
                      <p className="mt-1 whitespace-pre-line text-xs">
                        {step.detail}
                      </p>
                    </details>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            /* Older data-plane: no `step_lines`, render exactly as before. */
            summary.steps.length > 0 && (
              <ol className="ml-4 list-decimal space-y-0.5 text-muted-foreground">
                {summary.steps.map((step, i) => (
                  <li key={`${i}-${step}`}>{step}</li>
                ))}
              </ol>
            )
          )}
          {summary.touches.length > 0 && (
            <div>
              <span className="text-muted-foreground">Touches: </span>
              {summary.touches.join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ViewWorkflowTabs({
  workflow,
  workflowKey,
  definitionId,
  summary,
  onPublished,
}: {
  workflow: DslWorkflow;
  workflowKey: string;
  definitionId: string;
  summary?: DefinitionSummary | null;
  onPublished?: (definition: DefinitionResponse) => void;
}) {
  const [tab, setTab] = useState<TabName>("canvas");
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Same adapter the authoring surface uses, so the Inspector sees an
  // identical configMap shape — no special-casing for read-only.
  const { nodes, edges } = useMemo(() => {
    const raw = toFlowGraph(workflow);
    return layoutFlowGraph(raw);
  }, [workflow]);
  const configMap = useMemo(() => configMapFromWorkflow(workflow), [workflow]);
  const issues = useMemo(() => validateTopology(workflow), [workflow]);

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as TabName)}>
      <TabsList>
        <TabsTrigger value="canvas">Canvas</TabsTrigger>
        <TabsTrigger value="triggers">Triggers</TabsTrigger>
        <TabsTrigger value="config">Config</TabsTrigger>
        <TabsTrigger value="json">JSON</TabsTrigger>
      </TabsList>
      <TabsContent value="canvas" className="pt-2">
        <div className="grid grid-cols-[1fr_320px] gap-2 items-start">
          <div className="border rounded-md overflow-hidden">
            <ReadOnlyCanvas
              workflow={workflow}
              height={420}
              onNodeSelect={setSelectedNodeId}
            />
          </div>
          <Inspector
            selectedNodeId={selectedNodeId}
            selectedEdgeId={null}
            nodes={nodes}
            edges={edges}
            configMap={configMap}
            onConfigChange={() => undefined}
            onEdgeLabelChange={() => undefined}
            readOnly
            workflow={workflow}
            summary={summary}
            overviewIssues={issues}
            onSelectIssueNode={setSelectedNodeId}
            workflowKey={workflowKey}
          />
        </div>
      </TabsContent>
      <TabsContent value="triggers" className="pt-2">
        <TriggersPanel
          workflowKey={workflowKey}
          definitionId={definitionId}
          triggers={workflow.triggers}
          onPublished={onPublished}
        />
      </TabsContent>
      <TabsContent value="config" className="pt-2">
        <WorkflowConfigPanel workflowKey={workflowKey} />
      </TabsContent>
      <TabsContent value="json" className="pt-2">
        <div className="rounded-md border">
          <Editor
            height="420px"
            defaultLanguage="json"
            value={JSON.stringify(workflow, null, 2)}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              fontSize: 12,
            }}
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}
